import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { inr, todayStr, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Stepper } from "@/components/Stepper";
import { toast } from "sonner";
import { ClipboardList, ShoppingBag } from "lucide-react";

const FIELDS = {
  NC: [
    { k: "consumer_name", l: "Consumer name" }, { k: "mobile", l: "Mobile" },
    { k: "connection_type", l: "Type (Domestic/Commercial)" }, { k: "regulator_number", l: "Regulator number" },
    { k: "regulator_deposit", l: "Regulator deposit", n: 1 }, { k: "cylinder_deposit", l: "Cylinder deposit", n: 1 },
    { k: "admin_charges", l: "Admin charges", n: 1 },
  ],
  TV_IN: [{ k: "consumer_no", l: "Consumer number" }, { k: "from_agency", l: "From agency" }, { k: "cylinder_deposit", l: "Cylinder deposit", n: 1 }, { k: "regulator_deposit", l: "Regulator deposit", n: 1 }],
  TV_OUT: [{ k: "consumer_no", l: "Consumer number" }, { k: "to_agency", l: "To agency" }, { k: "cylinder_deposit", l: "Cylinder deposit", n: 1 }, { k: "regulator_deposit", l: "Regulator deposit", n: 1 }],
  MI: [{ k: "consumer_no", l: "Consumer number" }, { k: "amount", l: "Amount collected", n: 1 }],
};
const LABELS = { NC: "New Connection", TV_IN: "Transfer In", TV_OUT: "Transfer Out", ARB: "Allied Retail", MI: "Inspection (MI)" };

function Module({ type }) {
  const [form, setForm] = useState({});
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/office/transactions?type=${type}`).then((r) => setRows(r.data));
  useEffect(() => { load(); setForm({}); }, [type]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { type, ...form };
      FIELDS[type].forEach((f) => { if (f.n && payload[f.k]) payload[f.k] = parseFloat(payload[f.k]); });
      await api.post("/office/transactions", payload);
      toast.success(`${LABELS[type]} saved`);
      setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5">
        <h3 className="font-semibold mb-3">{LABELS[type]}</h3>
        <div className="space-y-3">
          {FIELDS[type].map((f) => (
            <div key={f.k}>
              <Label>{f.l}</Label>
              <Input type={f.n ? "number" : "text"} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`office-${type}-${f.k}-input`} />
            </div>
          ))}
          <Button onClick={save} disabled={busy} className="w-full" data-testid="office-transaction-submit-button">Save</Button>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-semibold">Recent entries</h3></div>
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Details</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No entries yet.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{FIELDS[type].map((f) => r[f.k]).filter(Boolean).join(" · ")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function ArbSale() {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [customer, setCustomer] = useState("");
  const [sales, setSales] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/arb/items").then((r) => setItems(r.data));
    api.get(`/arb/sales?date=${todayStr()}`).then((r) => setSales(r.data));
  };
  useEffect(() => { load(); }, []);

  const sel = items.find((i) => i.id === itemId);
  useEffect(() => { if (sel) setAmount(String((sel.price || 0) * qty)); }, [itemId, qty]);

  const submit = async () => {
    if (!itemId) { toast.error("Choose an item"); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter the amount"); return; }
    setBusy(true);
    try {
      await api.post("/arb/sales", { item_id: itemId, qty, amount: parseFloat(amount), payment_mode: mode, customer_name: customer });
      toast.success("ARB sale recorded");
      setItemId(""); setQty(1); setAmount(""); setCustomer(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Sell an ARB item</h3>
        <div className="space-y-3">
          <div><Label>Item</Label>
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger data-testid="arb-sale-item-select"><SelectValue placeholder="Choose item" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {items.map((i) => (
                  <SelectItem key={i.id} value={i.id} disabled={i.stock <= 0}>
                    {i.name}{i.model ? ` (${i.model})` : ""} — stock {i.stock}{i.price ? ` · ${inr(i.price)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {sel && <div className="rounded-lg bg-secondary p-3 text-sm flex justify-between"><span>In stock: <b>{sel.stock}</b></span><span>Unit price: <b>{inr(sel.price)}</b></span></div>}
          <div className="flex items-center justify-between"><Label>Quantity</Label><Stepper value={qty} onChange={(v) => setQty(Math.max(1, v))} min={1} testid="arb-sale-qty" /></div>
          <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="arb-sale-amount-input" /></div>
          <div><Label>Customer (optional)</Label><Input value={customer} onChange={(e) => setCustomer(e.target.value)} /></div>
          <div><Label>Payment</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {["cash", "upi"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`arb-sale-mode-${m}`}
                  className={`rounded-xl border p-2.5 font-medium uppercase text-sm ${mode === m ? "border-primary bg-primary/10 text-primary" : "bg-card"}`}>{m}</button>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full" data-testid="arb-sale-submit-button">Record sale</Button>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-semibold">Today's ARB sales</h3></div>
        <div className="max-h-[460px] overflow-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Mode</TableHead></TableRow></TableHeader>
            <TableBody>
              {sales.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No sales today.</TableCell></TableRow>}
              {sales.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-sm">{s.item_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(s.amount)}</TableCell>
                  <TableCell className="uppercase text-xs">{s.payment_mode}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

export default function Office() {
  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><ClipboardList className="h-6 w-6 text-primary" /> Office entries</h1>
        <p className="text-sm text-muted-foreground">New connections, transfers, allied retail sales and inspections.</p>
      </div>
      <Tabs defaultValue="NC">
        <TabsList data-testid="office-transactions-tabs" className="flex flex-wrap h-auto">
          {Object.keys(LABELS).map((t) => <TabsTrigger key={t} value={t} data-testid={`office-tab-${t}`}>{LABELS[t]}</TabsTrigger>)}
        </TabsList>
        {["NC", "TV_IN", "TV_OUT", "MI"].map((t) => <TabsContent key={t} value={t} className="mt-4"><Module type={t} /></TabsContent>)}
        <TabsContent value="ARB" className="mt-4"><ArbSale /></TabsContent>
      </Tabs>
    </div>
  );
}
